import { log } from './logger';

export const getBaseURL = () => {
  // Check if we're in Capacitor (native app)
  // Capacitor uses capacitor:// protocol, and origin is typically capacitor://localhost
  const isCapacitor = typeof window !== 'undefined' && (
    window.location.protocol === 'capacitor:' ||
    window.location.origin === 'capacitor://localhost' ||
    window.location.href.startsWith('capacitor://')
  );

  // Debug logging to help diagnose issues
  if (typeof window !== 'undefined') {
    log.debug("API", "Environment check:", {
      isCapacitor,
      protocol: window.location.protocol,
      origin: window.location.origin,
      href: window.location.href.substring(0, 50),
      hasViteApiUrl: !!import.meta.env.VITE_API_URL,
      isProd: import.meta.env.PROD,
      isDev: import.meta.env.DEV
    });
  }

  if (isCapacitor) {
    // In Capacitor, we MUST use VITE_API_URL - capacitor://localhost is not a valid backend URL
    const apiUrl = import.meta.env.VITE_API_URL;
    if (!apiUrl) {
      const errorMsg = "VITE_API_URL is required in Capacitor/native builds but is not set! " +
                      "Please configure VITE_API_URL in your build environment.";
      log.error("API", errorMsg);
      log.error("API", "Current window.location:", {
        origin: window.location.origin,
        protocol: window.location.protocol,
        href: window.location.href
      });
      // Don't throw - instead use a fallback that will show the error clearly
      log.warn("API", "Falling back to http://localhost:8000 - this may not work in production!");
      return "http://localhost:8000";
    }
    log.debug("API", "Using base URL (Capacitor):", apiUrl);
    return apiUrl;
  }

  // For production web builds, VITE_API_URL is REQUIRED (no fallback to origin)
  if (import.meta.env.PROD) {
    // Safety check: if origin is capacitor://localhost, we're definitely in Capacitor
    // even if the detection above failed
    if (window.location.origin === 'capacitor://localhost' || window.location.href.startsWith('capacitor://')) {
      const apiUrl = import.meta.env.VITE_API_URL;
      if (!apiUrl) {
        log.error("API", "CRITICAL: Running in Capacitor but VITE_API_URL was not set at build time!");
        log.error("API", "VITE_API_URL must be set when running 'npm run build' before syncing to iOS.");
        log.error("API", "Example: VITE_API_URL=https://your-backend.com npm run build");
        throw new Error("VITE_API_URL is required in Capacitor production builds. Please set it when building.");
      }
      log.debug("API", "Using base URL (Capacitor, detected in PROD path):", apiUrl);
      return apiUrl;
    }

    // CRITICAL: In production, VITE_API_URL is REQUIRED
    // Never fall back to window.location.origin - frontend and backend are on different domains
    const apiUrl = import.meta.env.VITE_API_URL;
    if (!apiUrl) {
      const errorMsg = "CRITICAL: VITE_API_URL is required in production but is not set! " +
                      "Please configure VITE_API_URL in your deployment environment (e.g., Render dashboard). " +
                      "Example: VITE_API_URL=https://api.athletespace.ai";
      log.error("API", errorMsg);
      log.error("API", "Current window.location.origin:", window.location.origin);
      log.error("API", "This will cause API calls to fail. Please set VITE_API_URL environment variable.");
      // Throw error to fail fast - prevents app from running with wrong API URL
      // This forces deployment to be configured correctly before the app can start
      throw new Error("VITE_API_URL environment variable is required in production. Please configure it in your deployment settings.");
    }
    log.debug("API", "Using base URL:", apiUrl);
    return apiUrl;
  }
  return "http://localhost:8000";
};
